import { RefObject, useEffect, useRef } from 'react';

export interface PinchRange {
  start: number;
  end: number;
}

interface UsePinchZoomOptions {
  rangeRef: RefObject<PinchRange>;
  onChange: (range: PinchRange) => void;
  onCommit: (range: PinchRange) => void;
  /** Horizontal inset(s) of the plotted area within the container. */
  padding?: number | { left: number; right: number };
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const MIN_SPAN_MS = HOUR_MS;
const MAX_SPAN_MS = 90 * DAY_MS;
const WHEEL_ZOOM_SENSITIVITY = 0.01;
const WHEEL_COMMIT_DELAY_MS = 200;

const distance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// A one-finger drag must pick a direction before hijacking the gesture, so
// vertical drags keep scrolling the page (touchAction: pan-y).
const PAN_DECIDE_THRESHOLD_PX = 8;

/**
 * Pinch-to-zoom and horizontal pan on a timeline. Zoom: two-finger pinch on
 * touch, trackpad pinch / Ctrl+wheel on desktop — the point under the
 * fingers/cursor stays anchored. Pan: one-finger horizontal drag on touch,
 * two-finger horizontal scroll on trackpad — the window shifts in time at the
 * current zoom's ms-per-pixel, so it tracks the gesture 1:1.
 */
export default function usePinchZoom(
  containerRef: RefObject<HTMLElement>,
  { rangeRef, onChange, onCommit, padding = 20 }: UsePinchZoomOptions,
) {
  const opts = useRef({ rangeRef, onChange, onCommit, padding });
  opts.current = { rangeRef, onChange, onCommit, padding };

  const g = useRef({
    active: false,
    initialDistance: 0,
    anchorTime: 0,
    gestureSpan: 0,
    contentLeft: 0,
    contentWidth: 1,
    latest: null as PinchRange | null,
    frame: null as number | null,
    commitTimer: null as ReturnType<typeof setTimeout> | null,
    pan: { active: false, decided: false, startX: 0, startY: 0, baseStart: 0, baseEnd: 0 },
  });

  const handlers = useRef<{
    bind: (el: HTMLElement) => void;
    unbind: (el: HTMLElement) => void;
  } | null>(null);

  if (!handlers.current) {
    const flush = () => {
      g.current.frame = null;
      if (g.current.latest) opts.current.onChange(g.current.latest);
    };

    const measure = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      const padding = opts.current.padding;
      const padLeft = typeof padding === 'number' ? padding : padding.left;
      const padRight = typeof padding === 'number' ? padding : padding.right;
      g.current.contentLeft = rect.left + padLeft;
      g.current.contentWidth = Math.max(rect.width - padLeft - padRight, 1);
    };

    const fractionAt = (clientX: number) =>
      clamp((clientX - g.current.contentLeft) / g.current.contentWidth, 0, 1);

    const scheduleChange = () => {
      if (g.current.frame === null) g.current.frame = requestAnimationFrame(flush);
    };

    const onTouchStart = (e: TouchEvent) => {
      // An uncommitted gesture's range (latest) is the base of the next one, so
      // chained gestures don't snap back to the last committed range.
      const range = g.current.latest ?? opts.current.rangeRef.current;
      if (!range) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        measure(e.currentTarget as HTMLElement);
        const [t0, t1] = [e.touches[0], e.touches[1]];
        g.current.initialDistance = distance(t0, t1) || 1;
        g.current.gestureSpan = range.end - range.start;
        const f = fractionAt((t0.clientX + t1.clientX) / 2);
        g.current.anchorTime = range.start + f * g.current.gestureSpan;
        g.current.active = true;
        g.current.pan.active = false;
        return;
      }
      if (e.touches.length === 1) {
        // No preventDefault yet: the drag direction decides between pan
        // (horizontal, handled here) and page scroll (vertical, browser).
        measure(e.currentTarget as HTMLElement);
        const touch = e.touches[0];
        g.current.pan = {
          active: true,
          decided: false,
          startX: touch.clientX,
          startY: touch.clientY,
          baseStart: range.start,
          baseEnd: range.end,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (g.current.pan.active && !g.current.active && e.touches.length === 1) {
        const touch = e.touches[0];
        const dx = touch.clientX - g.current.pan.startX;
        const dy = touch.clientY - g.current.pan.startY;
        if (!g.current.pan.decided) {
          if (Math.abs(dx) < PAN_DECIDE_THRESHOLD_PX && Math.abs(dy) < PAN_DECIDE_THRESHOLD_PX) return;
          if (Math.abs(dx) <= Math.abs(dy)) {
            g.current.pan.active = false;
            return;
          }
          g.current.pan.decided = true;
        }
        e.preventDefault();
        const span = g.current.pan.baseEnd - g.current.pan.baseStart;
        // Dragging right pulls earlier dates into view (direct manipulation).
        const shiftMs = -dx * (span / g.current.contentWidth);
        g.current.latest = {
          start: g.current.pan.baseStart + shiftMs,
          end: g.current.pan.baseEnd + shiftMs,
        };
        scheduleChange();
        return;
      }
      if (!g.current.active || e.touches.length !== 2) return;
      e.preventDefault();
      const [t0, t1] = [e.touches[0], e.touches[1]];
      const scale = distance(t0, t1) / g.current.initialDistance;
      const f = fractionAt((t0.clientX + t1.clientX) / 2);
      const newSpan = clamp(g.current.gestureSpan / scale, MIN_SPAN_MS, MAX_SPAN_MS);
      g.current.latest = {
        start: g.current.anchorTime - f * newSpan,
        end: g.current.anchorTime + (1 - f) * newSpan,
      };
      scheduleChange();
    };

    const endGesture = () => {
      if (!g.current.active && !g.current.pan.active) return;
      g.current.active = false;
      g.current.pan.active = false;
      g.current.pan.decided = false;
      if (g.current.frame !== null) {
        cancelAnimationFrame(g.current.frame);
        g.current.frame = null;
      }
      if (g.current.latest) {
        opts.current.onCommit(g.current.latest);
        g.current.latest = null;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (g.current.active && e.touches.length < 2) {
        endGesture();
        return;
      }
      if (g.current.pan.active && e.touches.length === 0) endGesture();
    };

    // iOS Safari pinch-zoom rides its own `gesture*` events that touchmove's
    // preventDefault doesn't suppress; without this the whole page zooms.
    const preventGesture = (e: Event) => e.preventDefault();

    // Wheel has no "end": commit once it goes idle.
    const scheduleWheelCommit = () => {
      if (g.current.commitTimer !== null) clearTimeout(g.current.commitTimer);
      g.current.commitTimer = setTimeout(() => {
        g.current.commitTimer = null;
        if (g.current.latest) {
          opts.current.onCommit(g.current.latest);
          g.current.latest = null;
        }
      }, WHEEL_COMMIT_DELAY_MS);
    };

    const onWheel = (e: WheelEvent) => {
      const base = g.current.latest ?? opts.current.rangeRef.current;
      if (!base) return;
      // Trackpad pinch / Ctrl+wheel arrive as wheel events with ctrlKey → zoom.
      if (e.ctrlKey) {
        e.preventDefault();
        measure(e.currentTarget as HTMLElement);
        const f = fractionAt(e.clientX);
        const span = base.end - base.start;
        const anchor = base.start + f * span;
        const factor = Math.exp(e.deltaY * WHEEL_ZOOM_SENSITIVITY);
        const newSpan = clamp(span * factor, MIN_SPAN_MS, MAX_SPAN_MS);
        g.current.latest = {
          start: anchor - f * newSpan,
          end: anchor + (1 - f) * newSpan,
        };
        scheduleChange();
        scheduleWheelCommit();
        return;
      }
      // Two-finger horizontal trackpad scroll → pan. Vertical-dominant wheel
      // events are left to the page scroll.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      measure(e.currentTarget as HTMLElement);
      const span = base.end - base.start;
      // deltaMode 1 = lines (Firefox); approximate a line as 16px.
      const deltaPx = e.deltaMode === 1 ? e.deltaX * 16 : e.deltaX;
      // Scrolling right moves the window forward in time, at ms-per-pixel of
      // the current zoom — the pan speed follows the visible span.
      const shiftMs = deltaPx * (span / g.current.contentWidth);
      g.current.latest = {
        start: base.start + shiftMs,
        end: base.end + shiftMs,
      };
      scheduleChange();
      scheduleWheelCommit();
    };

    handlers.current = {
      bind: (el) => {
        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
        el.addEventListener('gesturestart', preventGesture, { passive: false });
        el.addEventListener('gesturechange', preventGesture, { passive: false });
        el.addEventListener('gestureend', preventGesture, { passive: false });
        el.addEventListener('wheel', onWheel, { passive: false });
      },
      unbind: (el) => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('touchcancel', onTouchEnd);
        el.removeEventListener('gesturestart', preventGesture);
        el.removeEventListener('gesturechange', preventGesture);
        el.removeEventListener('gestureend', preventGesture);
        el.removeEventListener('wheel', onWheel);
      },
    };
  }

  const boundEl = useRef<HTMLElement | null>(null);

  // Re-bind when the element changes (it may mount late, behind a loading guard).
  useEffect(() => {
    const el = containerRef.current;
    if (el === boundEl.current) return;
    if (boundEl.current) handlers.current!.unbind(boundEl.current);
    boundEl.current = el;
    if (el) handlers.current!.bind(el);
  });

  // Reset boundEl on teardown so a StrictMode remount re-binds.
  useEffect(
    () => () => {
      if (boundEl.current) handlers.current!.unbind(boundEl.current);
      boundEl.current = null;
      if (g.current.frame !== null) cancelAnimationFrame(g.current.frame);
      if (g.current.commitTimer !== null) clearTimeout(g.current.commitTimer);
    },
    [],
  );
}
